import { FC, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@mui/material'

import { HeaderGameIcon, HeaderGenreIcon } from '../../helpers/deltaHeaderIcons'
import { differentiationDelta, getPatternMixRows, getTopRisingPatterns } from '../../helpers/showcase'
import { useGamePageNavigation } from '../../hooks/useGamePageNavigation'
import { useGameStoryPatterns } from '../../hooks/useGameStoryPatterns'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { StoryPattern } from '../../types'
import { EmptyStateCard } from '../EmptyStateCard/EmptyStateCard'
import { PatternMixChart } from '../PatternMixChart/PatternMixChart'
import { RisingStoryPatterns } from '../RisingStoryPatterns/RisingStoryPatterns'
import { SectionCard } from '../SectionCard/SectionCard'
import type { ShowcaseCrumb } from '../ShowcaseBreadcrumb/ShowcaseBreadcrumb'
import { ShowcaseBreadcrumb } from '../ShowcaseBreadcrumb/ShowcaseBreadcrumb'
import { StoryPatternDetailView } from '../StoryPatternDetailView/StoryPatternDetailView'
import { StoryPatternsTable } from '../StoryPatternsTable/StoryPatternsTable'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'

interface GameStoryPatternsTabProps {
  gameId: string | null
}

/**
 * "Story Patterns" tab for the UA Insights game page. Shows the game's own
 * narrative-pattern mix benchmarked against its genre, and a game-scoped
 * drill-down (reusing `StoryPatternDetailView`). Drill-down state lives in the
 * `pattern` / `lens` query params so it stays on the game route and is
 * shareable, consistent with the page's `tab` param.
 */
export const GameStoryPatternsTab: FC<GameStoryPatternsTabProps> = ({ gameId }) => {
  const { t } = useTranslation('ua-insights')
  const { patternLabel, motivationLabel, genreLabel } = useUATaxonomy()
  const { patternById, motivationById } = useUAInsightsMeta()
  const { data, isLoading } = useGameStoryPatterns(gameId)
  const benchmarkLabel = data?.benchmarkGenre ? genreLabel(data.benchmarkGenre) : ''
  // In-page drill-down state + the shared game-page navigation contract.
  // `openPatternInTab` drills within this tab; `openMotivation` hands off to
  // the Motivations tab (e.g. clicking an "Associated Motivation" chip).
  const {
    activePatternId: activePattern,
    activeLensId: activeLens,
    openPatternInTab: openPattern,
    backToPatternTable: backToTable,
    openMotivation: openMotivationView,
  } = useGamePageNavigation()

  // Jump back to the top on any drill-down transition so the new view starts in
  // view rather than at the previous scroll position.
  useEffect(() => {
    const raf = requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'auto' }))
    return () => cancelAnimationFrame(raf)
  }, [activePattern, activeLens])

  // Only surface patterns the game actually uses (>=1 creative); a zero-creative
  // pattern exists in the genre taxonomy but isn't part of this game's mix, so it
  // shouldn't clutter the table or the Top Rising cards. Sub-patterns are pruned
  // the same way so expanded rows stay relevant.
  const gamePatterns = useMemo<StoryPattern[]>(
    () => (data ? data.patterns.filter((p) => p.creativesCount > 0).map((p) => ({ ...p, children: p.children?.filter((c) => c.creativesCount > 0) })) : []),
    [data]
  )

  const topRising = useMemo(() => getTopRisingPatterns(gamePatterns), [gamePatterns])

  // The App-vs-Genre mix is built from the full pattern list (not the
  // creatives>0 subset) so it can also surface patterns the genre uses but the
  // game skips entirely.
  const patternMixRows = useMemo(() => (data ? getPatternMixRows(data.patterns, patternLabel) : []), [data, patternLabel])

  // Both delta columns read "Monthly Δ"; the leading icon (the game's icon vs a
  // genre badge) distinguishes whose month-over-month change each column shows.
  const sourceIcon = useMemo(() => <HeaderGameIcon src={data?.game.icon} alt={data?.game.name ?? ''} />, [data?.game.icon, data?.game.name])

  // Memoised because `StoryPatternsTable` keys its row flattening and column
  // definitions off these objects: a fresh identity each render would rebuild
  // both on every parent render.
  const benchmark = useMemo(
    () => ({
      label: t('showcase_col_change'),
      icon: <HeaderGenreIcon label={benchmarkLabel} />,
      tooltip: t('compare_story_patterns_delta_tooltip', { name: benchmarkLabel }),
      accessor: (p: StoryPattern) => p.benchmark?.deltaPp,
    }),
    [t, benchmarkLabel]
  )

  // Trailing "Difference" column: the gap between the game's Monthly Δ and the
  // genre's Monthly Δ, surfacing where the game's trend diverges from its genre.
  const difference = useMemo(
    () => ({
      label: t('compare_story_patterns_diff_label'),
      accessor: differentiationDelta,
    }),
    [t]
  )

  if (isLoading || !data) {
    return <TableSkeleton rows={8} columns={6} />
  }

  if (!gamePatterns.length) {
    return <EmptyStateCard>{t('game_story_patterns_empty')}</EmptyStateCard>
  }

  if (activePattern && gameId != null) {
    const resolved = patternById(activePattern)
    const lens = activeLens ? motivationById(activeLens) : undefined
    const crumbs: ShowcaseCrumb[] = [{ label: t('game_story_patterns_breadcrumb_root'), onClick: backToTable }]
    // The lens crumb is the motivation the pattern was opened through, so it
    // drills out to that motivation's in-page view (Motivations tab), matching
    // the dashboard where the lens crumb navigates to the Motivation detail.
    if (activeLens && lens) crumbs.push({ label: motivationLabel(lens), onClick: () => openMotivationView(activeLens) })
    const parent = resolved?.parent
    if (parent) crumbs.push({ label: patternLabel(parent), onClick: () => openPattern(parent.id, activeLens) })
    if (resolved) crumbs.push({ label: patternLabel(resolved.pattern) })

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ShowcaseBreadcrumb crumbs={crumbs} />
        <StoryPatternDetailView
          patternId={activePattern}
          lensMotivationId={activeLens}
          gameId={gameId}
          onOpenPattern={openPattern}
          onOpenMotivation={openMotivationView}
        />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {patternMixRows.length > 0 && (
        <SectionCard title={t('pattern_mix_title', { game: data.game.name, benchmark: benchmarkLabel })}>
          <PatternMixChart
            rows={patternMixRows}
            gameName={data.game.name}
            benchmarkLabel={benchmarkLabel}
            onSelectPattern={(patternId) => openPattern(patternId)}
          />
        </SectionCard>
      )}

      {topRising.length > 0 && (
        <SectionCard title={t('showcase_top_rising_patterns')}>
          <RisingStoryPatterns patterns={topRising} onSelect={(patternId) => openPattern(patternId)} showGames={false} />
        </SectionCard>
      )}

      <SectionCard title={t('showcase_creative_story_patterns')} disableBodyPadding>
        <StoryPatternsTable
          patterns={gamePatterns}
          firstColumnLabel={t('showcase_col_story_pattern')}
          onSelectPattern={(patternId) => openPattern(patternId)}
          changeColumnIcon={sourceIcon}
          changeColumnTooltip={t('compare_story_patterns_delta_tooltip', { name: data.game.name })}
          benchmark={benchmark}
          difference={difference}
          hideTopGames
          disableCard
        />
      </SectionCard>
    </Box>
  )
}
